import { ThemeColors } from '@evrekadev/evreka-ui-components'
import { AIActions, ActionType } from '../types'
import { selectClientId } from 'services/slices/auth'
import { useSelector } from 'react-redux'
import { useAIContext } from '../context/AIProvider'
import { useGetEmployeeVehiclePerformanceQuery } from 'services/ai'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo } from 'react'
import { InputSkeleton } from 'components/Skeletons'
import { Flex } from 'components'
import { DataError } from '../components/DataError'
import Chart from 'react-apexcharts'
import { useTranslation } from 'utils/useTranslation'

const GarbageTruckIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={24}
    height={24}
    color={'#000000'}
    fill={'none'}
    {...props}
  >
    <circle cx="17" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="7" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M11 9V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M7 10V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M17 4H15M15 4H9.48528C8.25903 4 7.6459 4 7.09459 4.22836C6.54328 4.45672 6.10973 4.89027 5.24264 5.75736L3.46446 7.53554C2.74189 8.25811 2.3806 8.6194 2.1903 9.07883C2 9.53825 2 10.0492 2 11.0711V13C2 15.357 2 16.5355 2.73223 17.2678C3.2191 17.7546 3.90328 17.9178 5 17.9724M15 4V8C15 9.88562 15 10.8284 15.5858 11.4142C16.1716 12 17.1144 12 19 12H22V13C22 15.357 22 16.5355 21.2678 17.2678C20.7809 17.7546 20.0967 17.9178 19 17.9724M9 18H15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 7H16.6998C18.0581 7 18.7373 7 19.29 7.35371C19.8427 7.70742 20.1647 8.34811 20.8087 9.6295L22 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const VehiclePerformance: React.FC = () => {
  const { t } = useTranslation('pages.ai.legends')
  const clientId = useSelector(selectClientId)
  const { setData } = useAIContext()

  const { data, isLoading, isError } = useGetEmployeeVehiclePerformanceQuery(
    clientId ? { clientId } : skipToken,
  )

  useEffect(() => data && setData(JSON.stringify(data)), [data, setData])

  const parsedData = useMemo(() => {
    const vehicles: string[] = []
    const done: number[] = []
    const undone: number[] = []

    for (const item of data ?? []) {
      const { done_task_count, vehicle_name, task_count } = item
      vehicles.push(vehicle_name)
      done.push(done_task_count)
      undone.push(task_count - done_task_count)
    }
    return { vehicles, done, undone }
  }, [data])

  if (isLoading) {
    return (
      <Flex directionColumn="zero" gap={1}>
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
      </Flex>
    )
  }

  if (isError) {
    return <DataError />
  }
  return (
    <div>
      <Chart
        options={{
          xaxis: {
            categories: parsedData?.vehicles,
          },
          chart: {
            type: 'bar',
            stacked: true,
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
        }}
        series={[
          {
            name: t('done'),
            data: parsedData?.done,
            color: ThemeColors.pink500,
          },
          {
            name: t('undone'),
            data: parsedData?.undone,
            color: ThemeColors.grey400,
          },
        ]}
        type="bar"
        width={'100%'}
      />
    </div>
  )
}

export const vehiclePerformance: ActionType = {
  action: AIActions.VEHICLE_PERFORMANCE,
  title: 'vehicle_performance.title',
  description: 'vehicle_performance.description',
  icon: <GarbageTruckIcon height={40} width={40} color={ThemeColors.grey700} />,
  dataComponent: <VehiclePerformance />,
}
